import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { freightAPI } from '../api';
import { toast } from 'react-toastify';

const RequestsContext = createContext();

export const useRequests = () => {
  const context = useContext(RequestsContext);
  if (!context) {
    throw new Error('useRequests must be used within a RequestsProvider');
  }
  return context;
};

export const RequestsProvider = ({ children }) => {
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchRequests = useCallback(async (isManualRefresh = false, page = 1, pageSize = 10, filters = {}) => {
    try {
      if (isManualRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const response = await freightAPI.getAllRequests(page, pageSize, filters);
      const { items = [], pagination: paginationData } = response;

      // Find requests missing front_status and update them
      const updateMissingStatus = async () => {
        const updates = items.map(async (request) => {
          if (!request.front_status) {
            try {
              await freightAPI.updateRequest(request.request_id, { front_status: 'pending' });
              request.front_status = 'pending';
            } catch (err) {
              toast.error(`Failed to update front_status for request ${request.request_id}`);
            }
          }
        });
        await Promise.all(updates);
      };
      await updateMissingStatus();
      
      setRequests(items);
      setPagination(paginationData);
      setLastRefreshTime(new Date());
      setHasLoaded(true);
      
      if (isManualRefresh) {
        toast.success('Data refreshed successfully');
      }
    } catch (error) {
      console.error('Requests fetch error:', error);
      toast.error('Failed to load requests. Please try again.');
      setError(error.message || 'Failed to load requests. Please try again.');
      setRequests([]);
      setPagination(null);
      setHasLoaded(true);
    } finally {
      if (isManualRefresh) {
        setIsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const updateRequest = useCallback(async (requestId, updates) => {
    try {
      await freightAPI.updateRequest(requestId, updates);
      // Update local state immediately for better UX
      setRequests(prevRequests => 
        prevRequests.map(request => 
          request.request_id === requestId 
            ? { ...request, ...updates }
            : request
        )
      );
      return true;
    } catch (error) {
      console.error('Update request error:', error);
      toast.error('Failed to update request. Please try again.');
      return false;
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-load data once when the context mounts
  useEffect(() => {
    // Only auto-load once on mount; avoid re-triggering on error state changes
    if (!hasLoaded && !loading && !error) {
      fetchRequests();
    }
  }, [hasLoaded, loading, error, fetchRequests]);

  const value = {
    requests,
    pagination,
    loading,
    isRefreshing,
    lastRefreshTime,
    error,
    fetchRequests,
    updateRequest,
    clearError,
    hasData: requests.length > 0,
    hasLoaded
  };

  return (
    <RequestsContext.Provider value={value}>
      {children}
    </RequestsContext.Provider>
  );
};
